import { z } from 'zod';

export const loginSchema = z.object({
  email: z
    .string({ message: 'Campo obrigatório' })
    .trim()
    .toLowerCase()
    .min(1, 'Campo obrigatório')
    .check(z.email('Informe um e-mail válido'))
    .max(100, 'E-mail pode ter no máximo 100 caracteres')
    .refine((val) => (val.split('@')[0] ?? '').length <= 64, {
      message: 'Parte local do e-mail não pode exceder 64 caracteres',
    }),
  password: z
    .string({ message: 'Campo obrigatório' })
    .min(1, 'Campo obrigatório')
    .max(100, 'Senha pode ter no máximo 100 caracteres'),
});

export const authUserSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    email: z.string().check(z.email()).optional(),
    roles: z.array(z.string()).readonly().optional(),
    isProvisionalPassword: z.boolean().optional(),
  })
  .loose();

export const loginResponseSchema = z.object({
  user: authUserSchema,
  accessToken: z.string().min(10),
  refreshToken: z.string().min(10),
  expiresIn: z.number().optional(),
  tokenType: z.string().optional(),
});

export const refreshResponseSchema = z.object({
  accessToken: z.string().min(10),
  refreshToken: z.string().min(10),
});

export const forgotPasswordSchema = z.object({
  email: z
    .string({ message: 'E-mail é obrigatório' })
    .trim()
    .toLowerCase()
    .min(1, 'E-mail é obrigatório')
    .check(z.email('Informe um e-mail válido'))
    .max(100, 'E-mail pode ter no máximo 100 caracteres')
    .refine((val) => (val.split('@')[0] ?? '').length <= 64, {
      message: 'Parte local do e-mail não pode exceder 64 caracteres',
    }),
});

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'No mínimo 8 caracteres')
      .max(128, 'Senha muito longa')
      .check(z.regex(/[a-z]/, 'Letra minúscula'))
      .check(z.regex(/[A-Z]/, 'Letra maiúscula'))
      .check(z.regex(/\d/, 'Pelo menos um número'))
      .check(z.regex(/[^a-zA-Z0-9]/, 'Caractere especial')),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas devem ser iguais',
    path: ['confirmPassword'],
  });

// Tipo completo usado pelo hook/service (inclui o token da URL)
export const resetPasswordInputSchema = resetPasswordSchema.and(
  z.object({ token: z.string().min(1) }),
);

export type LoginInput = z.infer<typeof loginSchema>;
export type AuthUser = z.infer<typeof authUserSchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>;
